interface DispatchSubscriber {
  fn: () => void;
  minInterval: number;
  lastRun: number;
  id: number;
  type?: 'discovery' | 'general';
  discoveryDebounceMs?: number;
  lastDiscoveryUpdate?: number;
  pendingDiscoveryUpdate?: NodeJS.Timeout;
}

let dispatchSubscribers: DispatchSubscriber[] = [];

let dispatchNextID = 0;

let tickLock = false;
let lastUnlocked: Date | undefined;
let tickLockTail = false;
let tickTailTimeout: NodeJS.Timeout | undefined;
const tickMinInterval = 100;

const acquireTickLock = () => {
  if (tickLock) {
    tickLockTail = true;
    return false;
  }

  if (lastUnlocked !== undefined && Date.now() - lastUnlocked.getTime() < tickMinInterval) {
    tickLockTail = true;
    return false;
  }

  tickLock = true;

  return true;
};

const releaseTickLock = () => {
  tickLock = false;

  lastUnlocked = new Date();
};

const setTickLockTail = () => {
  if (tickTailTimeout !== undefined) {
    return;
  }

  tickTailTimeout = setTimeout(() => {
    // Free the handle before the lock check so a later burst can schedule again.
    // Per rule: settle-every-terminal-branch (ai.knowledge/global) — every fire must release the slot.
    tickTailTimeout = undefined;
    if (tickLockTail) {
      tickLockTail = false;
      tick();
    }
  }, tickMinInterval);
};

export const tick = () => {
  const ok = acquireTickLock();

  if (!ok) {
    return;
  }

  for (const subscriber of dispatchSubscribers) {
    try {
      if (Date.now() - subscriber.lastRun < subscriber.minInterval) {
        continue;
      }

      // Discovery subscribers are THROTTLED, not trailing-debounced: a trailing debounce
      // that reset its timer on every tick starved the refresh completely once discovery
      // became streaming (heartbeats/publishes/prunes never left a quiet gap), and a
      // camera discovered/fingerprinted right after the wizard opens would not appear
      // until a remount. Arm the flush ONCE per burst; later ticks do not reset it.
      if (subscriber.type === 'discovery' && subscriber.discoveryDebounceMs) {
        // A window is already armed — let it fire. Everything that arrived while it was
        // armed is coalesced into that one invocation, so we never reset it.
        if (subscriber.pendingDiscoveryUpdate) {
          continue;
        }

        // When this coalescing window opened.
        subscriber.lastDiscoveryUpdate = Date.now();

        // Guaranteed trailing fire at the end of the window. minInterval then gates how
        // soon the next window may open (the lastRun check above).
        subscriber.pendingDiscoveryUpdate = setTimeout(() => {
          try {
            subscriber.fn();
            subscriber.lastRun = Date.now();
          } catch (e) {
            console.error('Error in debounced discovery update:', e);
          }
          delete subscriber.pendingDiscoveryUpdate;
        }, subscriber.discoveryDebounceMs);

        continue;
      }

      // Regular subscribers execute immediately
      subscriber.fn();
      subscriber.lastRun = Date.now();
    } catch (e) {
      console.error(e);
    }
  }

  releaseTickLock();
  setTickLockTail();
};

interface SubscriberOptions {
  minInterval?: number;
  type?: 'discovery' | 'general';
  discoveryDebounceMs?: number;
}

export const addCentralDispatchSubscriber = (
  subscriber: () => void,
  options: number | SubscriberOptions = 1000
): number => {
  const id = dispatchNextID++;

  // Handle legacy number parameter or new options object
  const opts: SubscriberOptions = typeof options === 'number' ? { minInterval: options } : options;

  const subscriberConfig: DispatchSubscriber = {
    fn: subscriber,
    minInterval: opts.minInterval ?? 1000,
    lastRun: 0,
    id,
    type: opts.type ?? 'general',
    ...(opts.discoveryDebounceMs !== undefined && { discoveryDebounceMs: opts.discoveryDebounceMs })
  };

  dispatchSubscribers.push(subscriberConfig);

  return id;
};

// Convenience function specifically for discovery subscribers
export const addDiscoveryDispatchSubscriber = (
  subscriber: () => void,
  debounceMs = 2000,
  minInterval = 1000
): number => {
  return addCentralDispatchSubscriber(subscriber, {
    type: 'discovery',
    minInterval,
    discoveryDebounceMs: debounceMs
  });
};

export const clearCentralDispatchSubscriber = (id: number) => {
  // Find the subscriber to clear any pending timeouts
  const subscriber = dispatchSubscribers.find((s) => s.id === id);
  if (subscriber?.pendingDiscoveryUpdate) {
    clearTimeout(subscriber.pendingDiscoveryUpdate);
  }

  dispatchSubscribers = dispatchSubscribers.filter((subscriber) => subscriber.id !== id);
};
